import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Icon } from '../components/icons';
import { Badge, Field, Modal, PageHeader, ToneDot } from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api, MOCK_MODE } from '../lib/api';
import { errorMessage } from '../lib/errors';
import { formatDateTime, formatPhone } from '../lib/format';
import { qk, useWaConnection } from '../lib/queries';
import type { WaStatus } from '../lib/types';

const STATUS_LABEL: Record<string, string> = {
  connected: 'Bağlı',
  pairing: 'Eşleştirme bekliyor',
  connecting: 'Bağlanıyor',
  disconnected: 'Bağlı değil',
  degraded: 'Kısıtlı bağlantı',
  logged_out: 'Oturum kapatıldı',
  suspected_ban: 'Şüpheli ban',
  unavailable: 'Backend hazır değil'
};

function statusTone(status: string): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (status === 'connected') return 'ok';
  if (status === 'pairing' || status === 'connecting' || status === 'degraded') return 'warn';
  if (status === 'suspected_ban' || status === 'logged_out') return 'danger';
  return 'neutral';
}

function statusDot(status: string): 'ok' | 'warn' | 'danger' | 'muted' {
  const tone = statusTone(status);
  return tone === 'neutral' ? 'muted' : tone;
}

const EXPECTED_ENDPOINTS = [
  'GET  /api/wa/status  → { status, qr, pairingCode, phone }',
  'POST /api/wa/pair    → { method: "qr" | "code", phone? }',
  'POST /api/wa/logout  → oturumu kapat'
];

/** S2 — Bağlantı: QR / pairing code ile WhatsApp Web eşleştirme. */
export default function ConnectionPage() {
  const wa = useWaConnection();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pairMode, setPairMode] = useState<'qr' | 'code'>('qr');
  const [phone, setPhone] = useState('');
  const [confirmLogout, setConfirmLogout] = useState(false);

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: qk.wa });
  }

  const pair = useMutation({
    mutationFn: () => api.waPair({ method: pairMode, phone: pairMode === 'code' ? phone.trim() : undefined }),
    onSuccess: () => {
      toast.push(pairMode === 'qr' ? 'QR eşleştirme başlatıldı.' : 'Eşleştirme kodu istendi.', 'ok');
      refresh();
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const reconnect = useMutation({
    mutationFn: () => api.waReconnect(),
    onSuccess: () => {
      toast.push('Yeniden bağlanma istendi.', 'ok');
      refresh();
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const logout = useMutation({
    mutationFn: () => api.waLogout(),
    onSuccess: () => {
      toast.push('WhatsApp oturumu kapatıldı.', 'ok');
      setConfirmLogout(false);
      refresh();
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const status: WaStatus | undefined = wa.data;
  const state = status?.status ?? 'disconnected';
  const unavailable = status?.unavailable === true;

  return (
    <div>
      <PageHeader
        title="Bağlantı"
        subtitle="WhatsApp Web oturumu — QR okutarak veya telefonla eşleştirme kodu alarak bağlanın."
        actions={
          <>
            <span className="flex items-center gap-2 text-[12px] text-ink-2">
              <ToneDot tone={statusDot(state)} />
              {STATUS_LABEL[state] ?? state}
            </span>
            <button type="button" className="btn btn-sm" onClick={refresh} disabled={wa.isFetching}>
              <Icon name="refresh" size={14} />
              Durumu yenile
            </button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 p-6 min-[1100px]:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-4">
          {unavailable ? (
            <section className="panel p-5">
              <div className="flex items-center gap-2">
                <Badge tone="warn">Yakında</Badge>
                <span className="text-[11px] text-muted">Backend uçları geliştiriliyor</span>
              </div>
              <h2 className="mt-3 text-[14px]">WhatsApp bağlama uçları henüz yayınlanmadı</h2>
              <p className="mt-1 text-[12px] leading-5 text-ink-2">
                Bu ekran sözleşmeye göre hazır: uçlar yayınlandığında QR ve eşleştirme kodu otomatik olarak burada
                görünecek. Şimdilik aşağıdaki sözleşme bekleniyor.
              </p>
              <ul className="mt-4 space-y-1">
                {EXPECTED_ENDPOINTS.map((endpoint) => (
                  <li key={endpoint} className="mono rounded-[4px] border border-line bg-paper px-2 py-1 text-[11px] text-ink-2">
                    {endpoint}
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center gap-2">
                <button type="button" className="btn btn-sm" onClick={refresh} disabled={wa.isFetching}>
                  <Icon name="refresh" size={14} />
                  Tekrar dene
                </button>
                <span className="text-[11px] text-muted">
                  {MOCK_MODE ? 'Mock modda QR akışı simüle edilir.' : 'API 3200 üzerinde çalışıyor; uç eklenince bu sayfa canlanır.'}
                </span>
              </div>
            </section>
          ) : (
            <>
              <section className="panel">
                <div className="panel-head">
                  <h2 className="text-[13px]">Oturum durumu</h2>
                  <Badge tone={statusTone(state)}>{STATUS_LABEL[state] ?? state}</Badge>
                </div>
                <dl className="divide-y divide-line text-[12px]">
                  <div className="flex items-center justify-between px-4 py-2">
                    <dt className="text-muted">Numara</dt>
                    <dd className="mono">{status?.phone ? formatPhone(status.phone) : '—'}</dd>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2">
                    <dt className="text-muted">Cihaz</dt>
                    <dd className="mono">{status?.device || '—'}</dd>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2">
                    <dt className="text-muted">Bağlanma</dt>
                    <dd className="mono">{status?.connectedAt ? formatDateTime(status.connectedAt) : '—'}</dd>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2">
                    <dt className="text-muted">Çalışma modu</dt>
                    <dd>{status?.mode ?? '—'}</dd>
                  </div>
                </dl>
                <div className="flex items-center justify-between gap-2 border-t border-line px-4 py-3">
                  <p className="text-[11px] text-muted">
                    Panel yalnızca 127.0.0.1 üzerinde çalışır. Oturum bilgisi sunucuda şifreli saklanır.
                  </p>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => reconnect.mutate()}
                      disabled={reconnect.isPending}
                    >
                      <Icon name="refresh" size={14} />
                      Yeniden bağlan
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => setConfirmLogout(true)}
                      disabled={state !== 'connected' && state !== 'pairing'}
                    >
                      <Icon name="logout" size={14} />
                      Bağlantıyı kes
                    </button>
                  </div>
                </div>
              </section>

              <section className="panel">
                <div className="panel-head">
                  <h2 className="text-[13px]">Eşleştirme</h2>
                  <div role="group" aria-label="Eşleştirme yöntemi" className="flex items-center gap-1">
                    {(['qr', 'code'] as const).map((value) => (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={pairMode === value}
                        className={
                          'btn btn-sm ' + (pairMode === value ? 'border-signal bg-signal-soft text-signal' : 'border-transparent text-muted')
                        }
                        onClick={() => setPairMode(value)}
                      >
                        {value === 'qr' ? 'QR' : 'Kod'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="panel-body space-y-3">
                  {pairMode === 'code' ? (
                    <Field label="Telefon numarası" htmlFor="pair-phone" hint="Ülke koduyla: +90 5xx xxx xx xx">
                      <input
                        id="pair-phone"
                        className="input mono"
                        value={phone}
                        onChange={(event) => setPhone(event.target.value)}
                        placeholder="+90 551 888 58 86"
                      />
                    </Field>
                  ) : null}

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => pair.mutate()}
                    disabled={state === 'connected' || pair.isPending || (pairMode === 'code' && phone.trim().length < 10)}
                  >
                    <Icon name="phone" size={14} />
                    {pairMode === 'qr' ? 'QR ile bağlan' : 'Kod iste'}
                  </button>

                  <p className="text-[11px] leading-4 text-muted">
                    Telefonunuzda WhatsApp → Bağlı cihazlar → Cihaz bağla yolunu izleyin. QR kod yaklaşık 20 saniyede
                    yenilenir; bu ekran açıkken durum 5 saniyede bir otomatik yenilenir.
                  </p>
                </div>
              </section>
            </>
          )}
        </div>

        <aside className="panel p-4">
          <div className="eyebrow mb-2">Eşleştirme kodu / QR</div>
          {unavailable ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-[12px] text-muted">
              <Icon name="phone" size={22} className="text-line-strong" />
              <span>Uçlar hazır olduğunda QR burada görünecek.</span>
            </div>
          ) : state === 'connected' ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ok-soft text-ok">
                <Icon name="check" size={20} />
              </span>
              <div className="text-[13px] font-medium">WhatsApp bağlı</div>
              <div className="mono text-[12px] text-muted">{status?.phone ? formatPhone(status.phone) : '—'}</div>
            </div>
          ) : status?.qr ? (
            <div className="flex flex-col items-center gap-3">
              <div className="rounded-[4px] border border-line bg-surface p-2">
                <QRCodeSVG value={status.qr} size={220} level="M" bgColor="#ffffff" fgColor="#10161c" />
              </div>
              <p className="text-center text-[11px] text-muted">WhatsApp → Bağlı cihazlar → Cihaz bağla → QR okut</p>
            </div>
          ) : status?.pairingCode ? (
            <div className="flex flex-col items-center gap-2 py-8">
              <div className="mono text-[24px] tracking-[0.2em]">{status.pairingCode}</div>
              <p className="text-center text-[11px] text-muted">
                Telefonda WhatsApp → Bağlı cihazlar → Telefon numarasıyla bağlan → kodu girin.
              </p>
            </div>
          ) : state === 'pairing' || state === 'connecting' ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-[12px] text-muted">
              <Icon name="refresh" size={22} className="text-line-strong" />
              <span className="font-medium text-ink-2">Eşleştirme başlatıldı</span>
              <span>QR/kod hazır olduğunda burada görünecek. WhatsApp Web köprüsü hazırlanıyor…</span>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-[12px] text-muted">
              <Icon name="phone" size={22} className="text-line-strong" />
              <span>Eşleştirme başlatmak için “QR ile bağlan” veya “Kod iste” düğmesini kullanın.</span>
            </div>
          )}
        </aside>
      </div>

      <Modal
        open={confirmLogout}
        title="Bağlantıyı kes"
        onClose={() => setConfirmLogout(false)}
        footer={
          <>
            <button type="button" className="btn btn-sm" onClick={() => setConfirmLogout(false)}>
              Vazgeç
            </button>
            <button type="button" className="btn btn-danger btn-sm" onClick={() => logout.mutate()} disabled={logout.isPending}>
              Bağlantıyı kes
            </button>
          </>
        }
      >
        <p className="text-[13px]">
          WhatsApp oturumu sunucudan silinir; yeniden bağlanmak için QR okutmanız gerekir. Test modu ayarları korunur.
        </p>
      </Modal>
    </div>
  );
}
